import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import LeavePayslipCheck from '@/components/leave/LeavePayslipCheck';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Calendar, CheckCircle2, XCircle, Trash2, Pencil, Plus, Users, TrendingUp, Settings2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LEAVE_TYPES = [
  { value: 'annual', label: 'Annual (paid)' },
  { value: 'sick', label: 'Sick' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'family_responsibility', label: 'Family Responsibility' },
  { value: 'other', label: 'Other' },
];

const STATUS_TONE = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  declined: 'bg-rose-100 text-rose-800 border-rose-300',
  cancelled: 'bg-slate-100 text-slate-700 border-slate-300',
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const workingDays = (startISO, endISO) => {
  if (!startISO || !endISO) return 0;
  const s = new Date(startISO);
  const e = new Date(endISO);
  if (e < s) return 0;
  let d = new Date(s);
  let n = 0;
  while (d <= e) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) n += 1;
    d.setDate(d.getDate() + 1);
  }
  return n;
};

export default function LeaveManager() {
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // request or null for new
  const [form, setForm] = useState({
    staff_id: '',
    leave_type: 'annual',
    start_date: todayISO(),
    end_date: todayISO(),
    reason: '',
    auto_approve: true,
    status: 'pending',
    days_override: '',   // '' = auto from dates, number = explicit override
  });

  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState('');

  // Balance profile editor (Hire Date + Manual Adjustment + Used override)
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileTarget, setProfileTarget] = useState(null);
  const [profileForm, setProfileForm] = useState({ hire_date: '', adjustment: 0, adjustment_note: '', used_override: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bs, rs] = await Promise.all([
        axios.get(`${API}/leave/balance-summary`, { headers }),
        axios.get(`${API}/leave/requests`, { headers }),
      ]);
      setBalances(bs.data.rows || []);
      setRequests(rs.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load leave data');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const staffOptions = balances.map(b => ({ id: b.staff_id, name: b.staff_name }));
  const balanceByStaff = useMemo(
    () => Object.fromEntries(balances.map(b => [b.staff_id, b])), [balances],
  );

  const filtered = requests.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (staffFilter !== 'all' && r.staff_id !== staffFilter) return false;
    return true;
  });

  const openNew = () => {
    setEditing(null);
    setForm({
      staff_id: staffOptions[0]?.id || '',
      leave_type: 'annual',
      start_date: todayISO(),
      end_date: todayISO(),
      reason: '',
      auto_approve: true,
      status: 'pending',
      days_override: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      staff_id: r.staff_id,
      leave_type: r.leave_type,
      start_date: r.start_date,
      end_date: r.end_date,
      reason: r.reason || '',
      auto_approve: false,
      status: r.status,
      days_override: r.days_overridden ? String(r.days) : '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.staff_id) return toast.error('Select a staff member');
    if (!form.start_date || !form.end_date) return toast.error('Pick a date range');
    if (form.end_date < form.start_date) return toast.error('End date is before start date');
    setSaving(true);
    try {
      if (editing) {
        // Single PUT — status is optional and only sent when changed
        const payload = {
          leave_type: form.leave_type,
          start_date: form.start_date,
          end_date: form.end_date,
          reason: form.reason,
          days: form.days_override === '' ? null : Number(form.days_override),
        };
        if (form.status && form.status !== editing.status) payload.status = form.status;
        await axios.put(`${API}/leave/requests/${editing.id}`, payload, { headers });
        toast.success('Leave updated');
      } else {
        const payload = {
          staff_id: form.staff_id,
          leave_type: form.leave_type,
          start_date: form.start_date,
          end_date: form.end_date,
          reason: form.reason,
          auto_approve: form.auto_approve,
        };
        if (form.days_override !== '') payload.days = Number(form.days_override);
        await axios.post(`${API}/leave/requests`, payload, { headers });
        toast.success(form.auto_approve ? 'Leave booked & approved' : 'Leave request created');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const setStatus = async (r, newStatus, reason = '') => {
    try {
      const body = { status: newStatus };
      if (newStatus === 'declined' && reason) body.decline_reason = reason;
      await axios.put(`${API}/leave/requests/${r.id}`, body, { headers });
      toast.success(`Marked ${newStatus}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const askDecline = (r) => {
    setDeclineTarget(r);
    setDeclineReason('');
    setDeclineOpen(true);
  };

  const confirmDecline = async () => {
    if (!declineTarget) return;
    await setStatus(declineTarget, 'declined', declineReason);
    setDeclineOpen(false);
    setDeclineTarget(null);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete leave request for ${r.staff_name}?`)) return;
    try {
      await axios.delete(`${API}/leave/requests/${r.id}`, { headers });
      toast.success('Deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const openProfile = (b) => {
    setProfileTarget(b);
    setProfileForm({
      hire_date: b.hire_date || todayISO(),
      adjustment: b.adjustment || 0,
      adjustment_note: '',
      used_override: b.used_override != null ? String(b.used_override) : '',
    });
    setProfileOpen(true);
  };

  const saveProfile = async () => {
    if (!profileTarget) return;
    setProfileSaving(true);
    try {
      await axios.patch(`${API}/leave/staff/${profileTarget.staff_id}`, {
        hire_date: profileForm.hire_date,
        adjustment: Number(profileForm.adjustment) || 0,
        adjustment_note: profileForm.adjustment_note,
        used_override: profileForm.used_override === '' ? null : Number(profileForm.used_override),
      }, { headers });
      toast.success('Balance profile updated');
      setProfileOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setProfileSaving(false); }
  };

  const applyRegisterFigure = async (b) => {
    if (!window.confirm(`Replace ${b.staff_name}'s typed figure (${b.used_override}) with register + requests (${(b.used_auto || 0).toFixed(2)})?`)) return;
    try {
      await axios.patch(`${API}/leave/staff/${b.staff_id}`, { used_override: null }, { headers });
      toast.success('Balance now follows register + requests');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const previewDays = workingDays(form.start_date, form.end_date);
  const staffBal = form.staff_id ? balanceByStaff[form.staff_id] : null;
  const wouldOverdraw = form.leave_type === 'annual'
    && staffBal
    && form.status !== 'declined'
    && form.status !== 'cancelled'
    && previewDays > (staffBal.remaining + (editing?.status === 'approved' ? editing.days : 0));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="leave-manager-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Staff Leave Manager</h1>
            <p className="text-muted-foreground mt-1">
              Accrual: 1.25 days per month worked → 15 days per year. Approved <b>annual</b> leave is
              deducted from a staff member's remaining balance.
            </p>
          </div>
          <Button onClick={openNew} data-testid="new-leave-btn" className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-1" /> New Leave
          </Button>
        </div>

        {/* Balance Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              Balances by Staff
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="p-6 text-center text-muted-foreground">Loading…</div>
            ) : balances.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">No internal staff on file yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff</TableHead>
                    <TableHead className="text-center">Hire Date</TableHead>
                    <TableHead className="text-center">Months Worked</TableHead>
                    <TableHead className="text-right">Accrued</TableHead>
                    <TableHead className="text-right">Used (Paid / Unpaid)</TableHead>
                    <TableHead className="text-right">Adjustment</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead className="text-right">Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {balances.map(b => (
                    <TableRow key={b.staff_id} data-testid={`balance-row-${b.staff_id}`}>
                      <TableCell className="font-medium">{b.staff_name}</TableCell>
                      <TableCell className="text-center text-sm text-muted-foreground">{b.hire_date || '—'}</TableCell>
                      <TableCell className="text-center">{b.months_worked}</TableCell>
                      <TableCell className="text-right">{b.accrued.toFixed(2)}</TableCell>
                      <TableCell className="text-right text-rose-600">
                        {b.used.toFixed(2)}
                        {b.used_override != null && (
                          <span className="ml-1 text-[10px] text-amber-700" title="Manually overridden">(override)</span>
                        )}
                        {b.used_override != null && Math.abs((b.used_auto || 0) - b.used_override) > 0.001 && (
                          <div className="text-[10px] font-normal leading-tight text-slate-600" data-testid={`balance-register-figure-${b.staff_id}`}>
                            Register + requests: <b>{(b.used_auto || 0).toFixed(2)}</b>
                            <button type="button" className="ml-1 underline text-indigo-700" onClick={() => applyRegisterFigure(b)} data-testid={`balance-use-register-${b.staff_id}`}>use this</button>
                          </div>
                        )}
                        {b.used_override == null && (b.used_register_only || 0) > 0 && (
                          <div className="text-[10px] font-normal leading-tight text-amber-700">incl. {b.used_register_only} from register</div>
                        )}
                        {(b.unpaid_used || 0) > 0 && (
                          <div className="text-[10px] font-normal leading-tight">
                            <span className="text-emerald-700">{(b.paid_used || 0).toFixed(2)} paid</span>
                            {' · '}
                            <span className="text-rose-700 font-semibold">{b.unpaid_used.toFixed(2)} unpaid</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell
                        className={`text-right ${(b.adjustment || 0) > 0 ? 'text-emerald-600' : (b.adjustment || 0) < 0 ? 'text-rose-600' : 'text-muted-foreground'}`}
                        data-testid={`balance-adjustment-${b.staff_id}`}
                      >
                        {(b.adjustment || 0) > 0 ? '+' : ''}{(b.adjustment || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-700" data-testid={`balance-remaining-${b.staff_id}`}>
                        {b.remaining.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8"
                          onClick={() => openProfile(b)}
                          data-testid={`edit-balance-${b.staff_id}`}
                          title="Edit hire date / adjustment"
                        >
                          <Settings2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <LeavePayslipCheck headers={headers} />

        {/* Requests */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-600" />
                Leave Requests
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-9 w-40" data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={staffFilter} onValueChange={setStaffFilter}>
                  <SelectTrigger className="h-9 w-56" data-testid="staff-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All staff</SelectItem>
                    {staffOptions.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">No leave requests match your filters.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead className="text-center">Days</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(r => (
                    <TableRow key={r.id} data-testid={`leave-row-${r.id}`}>
                      <TableCell className="font-medium">{r.staff_name}</TableCell>
                      <TableCell className="capitalize">{r.leave_type.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1 text-slate-700">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          {r.start_date} → {r.end_date}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-semibold">
                        {r.days}
                        {r.days_overridden && (
                          <span className="ml-1 text-[10px] text-amber-700 font-normal" title="Days manually overridden">
                            (override)
                          </span>
                        )}
                        {r.status === 'approved' && r.leave_type === 'annual' && r.unpaid_days > 0 && (
                          <div className="text-[10px] font-normal mt-0.5 leading-tight">
                            <span className="text-emerald-700">{r.paid_days} paid</span>
                            {' · '}
                            <span className="text-rose-700">{r.unpaid_days} unpaid</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate text-sm text-muted-foreground" title={r.reason || ''}>
                        {r.reason || '—'}
                        {r.decline_reason && (
                          <div className="text-[11px] text-rose-600 italic">Decline: {r.decline_reason}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${STATUS_TONE[r.status] || ''} capitalize`}>
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {r.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 h-8"
                              onClick={() => setStatus(r, 'approved')}
                              data-testid={`approve-${r.id}`}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-rose-600 text-rose-700 hover:bg-rose-50 h-8"
                              onClick={() => askDecline(r)}
                              data-testid={`decline-${r.id}`}
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" /> Decline
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8"
                          onClick={() => openEdit(r)}
                          data-testid={`edit-${r.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 text-rose-600 hover:bg-rose-50"
                          onClick={() => remove(r)}
                          data-testid={`delete-${r.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="leave-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Leave' : 'New Leave'}</DialogTitle>
            <DialogDescription>
              Weekdays are counted as working days. Approved annual leave reduces the staff member's
              remaining balance automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Staff</label>
              <Select
                value={form.staff_id}
                onValueChange={(v) => setForm({ ...form, staff_id: v })}
                disabled={!!editing}
              >
                <SelectTrigger data-testid="dlg-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  {staffOptions.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {staffBal && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Current remaining: <b className="text-emerald-700">{staffBal.remaining.toFixed(2)}</b> days
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">Leave Type</label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="dlg-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Start Date</label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="dlg-start-date"
                />
              </div>
              <div>
                <label className="text-sm font-medium">End Date</label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="dlg-end-date"
                />
              </div>
            </div>

            <div className="rounded-md bg-slate-50 border p-2 text-sm">
              Working days (Mon–Fri): <b>{previewDays}</b>
              {wouldOverdraw && (
                <span className="ml-2 text-amber-700 text-xs">
                  ⚠️ Exceeds remaining ({staffBal.remaining.toFixed(2)} days). The extra <b>{(previewDays - staffBal.remaining).toFixed(2)}</b> day(s) will be logged as <b>UNPAID</b> and auto-deducted from gross on the next payslip.
                </span>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">Days (override) <span className="text-xs text-muted-foreground">— optional</span></label>
              <Input
                type="number"
                step="0.25"
                min="0"
                value={form.days_override}
                onChange={(e) => setForm({ ...form, days_override: e.target.value })}
                placeholder={`Auto: ${previewDays} (leave blank to keep auto)`}
                data-testid="dlg-days-override"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Use for half-days or manual corrections. Leave blank to auto-compute from the date range.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Reason</label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Optional"
                data-testid="dlg-reason"
              />
            </div>

            {editing ? (
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="dlg-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.auto_approve}
                  onChange={(e) => setForm({ ...form, auto_approve: e.target.checked })}
                  data-testid="dlg-auto-approve"
                />
                Auto-approve on save (deducts from remaining balance immediately)
              </label>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="dlg-save-btn" className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Balance profile (hire date + adjustment) */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent data-testid="profile-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Balance — {profileTarget?.staff_name}</DialogTitle>
            <DialogDescription>
              Hire date drives monthly accrual (1.25 days/month). Adjustment is a manual +/- number of
              days added on top of Accrued − Used (e.g. gifted days, carry-over, docked days).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Hire Date</label>
              <Input
                type="date"
                value={profileForm.hire_date}
                onChange={(e) => setProfileForm({ ...profileForm, hire_date: e.target.value })}
                data-testid="profile-hire-date"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Changing this recomputes Months Worked and Accrued immediately.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Manual Adjustment (days)</label>
              <Input
                type="number"
                step="0.25"
                value={profileForm.adjustment}
                onChange={(e) => setProfileForm({ ...profileForm, adjustment: e.target.value })}
                placeholder="e.g. 2 (bonus) or -1 (docked)"
                data-testid="profile-adjustment"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Positive = extra days; negative = deduct. Applies directly to Remaining.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Used (override)</label>
              <Input
                type="number"
                step="0.25"
                min="0"
                value={profileForm.used_override}
                onChange={(e) => setProfileForm({ ...profileForm, used_override: e.target.value })}
                placeholder={`Auto: ${profileTarget?.used_auto?.toFixed(2) ?? profileTarget?.used?.toFixed(2) ?? '0.00'} (leave blank to keep auto)`}
                data-testid="profile-used-override"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Replaces the auto-summed Used total. Leave blank to revert to the sum of approved annual leave.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Note (optional)</label>
              <Input
                value={profileForm.adjustment_note}
                onChange={(e) => setProfileForm({ ...profileForm, adjustment_note: e.target.value })}
                placeholder="Reason for adjustment"
                data-testid="profile-note"
              />
            </div>
            {profileTarget && (
              <div className="rounded-md bg-slate-50 border p-2 text-xs text-slate-700">
                Current: Accrued <b>{profileTarget.accrued.toFixed(2)}</b> · Used <b className="text-rose-600">{profileTarget.used.toFixed(2)}</b>
                {profileTarget.used_override != null && <span className="text-amber-700"> (override)</span>}
                {' · '}Adj <b>{(profileTarget.adjustment || 0).toFixed(2)}</b> · Remaining <b className="text-emerald-700">{profileTarget.remaining.toFixed(2)}</b>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileOpen(false)}>Cancel</Button>
            <Button
              onClick={saveProfile}
              disabled={profileSaving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="profile-save-btn"
            >
              {profileSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline reason */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle>Decline Leave Request</DialogTitle>
            <DialogDescription>Optional note visible to the requester.</DialogDescription>
          </DialogHeader>
          <Input
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Reason (optional)"
            data-testid="decline-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={confirmDecline} data-testid="decline-confirm-btn">
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
